import type { HireJobDraft } from '@features/buyer/hire/hireJobContextTypes';
import { getCategoryTypeOptions } from '@features/buyer/hire/job-price-calculator/category-pickers';
import {
  CATEGORY_TYPE,
  type CategoryType,
  categoryTypeOf,
  filterCategoriesByType,
  isAutomotiveCategory,
  soleEntry,
} from '@features/buyer/hire/job-price-calculator/priceCalculator';
import { FEATURE_FLAG_KEYS, useFeatureFlag } from '@lib/featureFlags';
import { useEffect, useState } from 'react';

import type { Category, SubCategory } from './jobPriceCalculatorTypes';

type UseJobPriceCategorySelectionArgs = {
  allCategories: Category[];
  createJob: HireJobDraft;
  saveJob: (patch: Partial<HireJobDraft>) => void;
  setAskingPrice: (value: string) => void;
  setTermsAccepted: (value: boolean) => void;
};

export const useJobPriceCategorySelection = ({
  allCategories,
  createJob,
  saveJob,
  setAskingPrice,
  setTermsAccepted,
}: UseJobPriceCategorySelectionArgs) => {
  const nonAutomotiveEnabled = useFeatureFlag(
    FEATURE_FLAG_KEYS.NON_AUTOMOTIVE_CATEGORY
  );
  const [typeOpen, setTypeOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [subCategoryOpen, setSubCategoryOpen] = useState(false);
  const [selectedCategoryType, setSelectedCategoryType] =
    useState<CategoryType | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  );
  const [selectedSubCategory, setSelectedSubCategory] =
    useState<SubCategory | null>(null);

  const clearPricing = () => {
    setAskingPrice('');
  };

  const filteredCategories = filterCategoriesByType(
    allCategories,
    selectedCategoryType
  );

  useEffect(() => {
    if (createJob.categoryId && allCategories.length > 0) {
      const savedCategory = allCategories.find(
        (cat) => cat.id === createJob.categoryId
      );
      if (savedCategory) {
        setSelectedCategoryType(categoryTypeOf(savedCategory));
        setSelectedCategory(savedCategory);
        if (createJob.subCategoryId && savedCategory.subCategories) {
          const savedSubCategory = savedCategory.subCategories.find(
            (sub) => sub.id === createJob.subCategoryId
          );
          if (savedSubCategory) {
            setSelectedSubCategory(savedSubCategory);
          }
        }
      }
    }
    if (createJob.askingPrice !== '' && createJob.askingPrice != null) {
      setAskingPrice(createJob.askingPrice);
    }
    if (createJob.termsAccepted) {
      setTermsAccepted(createJob.termsAccepted);
    }
  }, [createJob, allCategories, setAskingPrice, setTermsAccepted]);

  const handleSelectedCategoryType = (type: CategoryType): void => {
    setSelectedCategoryType(type);
    clearPricing();
    setTypeOpen(false);
    const isAutomotive = type === CATEGORY_TYPE.AUTOMOTIVE;

    saveJob({
      isAutomotive,
      lineOfBusiness: null,
      categoryId: null,
      subCategoryId: null,
    });

    const onlyCategory = soleEntry(filterCategoriesByType(allCategories, type));

    if (onlyCategory) {
      setSelectedCategory(onlyCategory);

      saveJob({
        isAutomotive: isAutomotiveCategory(onlyCategory),
        lineOfBusiness: onlyCategory.lineOfBusiness || null,
        categoryId: onlyCategory.id ?? null,
        subCategoryId: null,
      });

      const onlySubCategory = soleEntry(onlyCategory.subCategories);
      if (onlySubCategory) {
        setSelectedSubCategory(onlySubCategory);
        saveJob({ subCategoryId: onlySubCategory.id });
      } else {
        setSelectedSubCategory(null);
      }
    } else {
      setSelectedCategory(null);
      setSelectedSubCategory(null);
    }
  };

  useEffect(() => {
    const options = getCategoryTypeOptions();
    if (
      options.length === 1 &&
      !selectedCategoryType &&
      !createJob.categoryId
    ) {
      handleSelectedCategoryType(options[0].value);
    }
    // eslint-disable-line react-hooks/exhaustive-deps
  }, [nonAutomotiveEnabled]);

  const handleSelectedCategory = (category: Category): void => {
    clearPricing();
    setSelectedCategory(category);
    setCategoryOpen(false);

    saveJob({
      isAutomotive: isAutomotiveCategory(category),
      lineOfBusiness: category.lineOfBusiness || null,
      categoryId: category.id ?? null,
      subCategoryId: null,
    });

    const onlySubCategory = soleEntry(category.subCategories);
    if (onlySubCategory) {
      setSelectedSubCategory(onlySubCategory);
      saveJob({ subCategoryId: onlySubCategory.id });
    } else {
      setSelectedSubCategory(null);
    }
  };

  const handleSelectedSubCategory = (subCategory: SubCategory): void => {
    clearPricing();
    setSelectedSubCategory(subCategory);
    setSubCategoryOpen(false);
    saveJob({ subCategoryId: subCategory.id ?? null });
  };

  return {
    pickers: {
      typeOpen,
      categoryOpen,
      subCategoryOpen,
      setTypeOpen,
      setCategoryOpen,
      setSubCategoryOpen,
      openType: () => setTypeOpen(true),
      openCategory: () => setCategoryOpen(true),
      openSubCategory: () => setSubCategoryOpen(true),
    },
    selectedCategoryType,
    selectedCategory,
    selectedSubCategory,
    filteredCategories,
    handleSelectedCategoryType,
    handleSelectedCategory,
    handleSelectedSubCategory,
  };
};
